import { LinearProgress } from '@mui/material'
import BackComponent from 'components/back'
import { CalendarComponent } from 'components/calendar'
import ToastComponent from 'components/toast'
import WizardComponent from 'components/wizard'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CalendarInputProps, CalendarType, CarpoolOptionProps } from 'types/CalendarTypes'
import { MessageProps } from 'types/CommonTypes'
import { DateCarpoolProps, DefaultCarpoolType } from 'types/CommunityTypes'
import { StudentProps } from 'types/StudentTypes'
import { UserOutputProps, UsersOutputProps } from 'types/UserTypes'
import { generateCalendarEvents } from 'utils/calendar'
import { getCommunityCarpoolTypeLabel, updateCommunityStudentCarpools } from 'utils/communities'
import { isWeb, titleCaseSchoolNameForUI } from 'utils/constants'
import { getUsers } from 'utils/users'
import './index.css'

const CarpoolManagementComponent: React.FC<{
  studentId: string
  user: UserOutputProps
  userStudents: StudentProps[]
  onUpdate: (cb?: () => void) => void
  failedAvatarUrls: Set<string>
  onFailedAvatarUrl: (url: string) => void
}> = ({ studentId, user, userStudents, onUpdate, failedAvatarUrls, onFailedAvatarUrl }) => {
  // 1. State Hooks
  const [selectedCarpoolType, setSelectedCarpoolType] = useState<string>(DefaultCarpoolType.DISMISSAL)
  const [communityUsers, setCommunityUsers] = useState<UsersOutputProps[] | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<string>('')
  const [msg, setMsg] = useState<MessageProps>()

  // 2. Derived Constants
  const { phoneNumber } = user
  const student: StudentProps | undefined = userStudents.find((s: StudentProps) => s.studentId === studentId)
  const comId = student?.schoolName

  // 3. Effect Hooks
  useEffect(() => {
    if (!comId) return

    const loadData = async () => {
      setIsLoading('Loading users and rooms...')
      const usersRes = await getUsers(comId)
      if (usersRes.data) {
        setCommunityUsers((usersRes.data.users as UsersOutputProps[]) ?? [])
      }
      setIsLoading('')
    }

    loadData()
  }, [comId])

  // 4. Derived State, Validations and Early Returns
  if (!comId || !communityUsers) return <LinearProgress />
  const carpoolsKey = selectedCarpoolType === DefaultCarpoolType.ARRIVAL ? 'arrivalCarpools' : 'dismissalCarpools'
  const carpools: DateCarpoolProps = student?.[carpoolsKey] ?? {}
  const events: CalendarInputProps[] = generateCalendarEvents(CalendarType.CARPOOL, {
    defaultTime: -1,
    currentUserId: phoneNumber,
    carpools,
    communityUsers,
  })
  const options: CarpoolOptionProps = {
    studentId,
    user,
    userStudents,
    poolType: selectedCarpoolType,
    communityUsers,
  }
  if (isLoading || !communityUsers.length) return <LinearProgress />

  // 5. Event Handlers
  const handleSetCustomSchedule = async (carpools: DateCarpoolProps): Promise<any> => {
    const carpoolCount = Object.keys(carpools).length
    const carpoolCountString = `${carpoolCount} ${carpoolCount > 1 ? 'carpools' : 'carpool'}`
    setIsLoading(`Updating ${carpoolCountString}...`)
    const { msg, error } = await updateCommunityStudentCarpools(comId, studentId, {
      carpoolType: selectedCarpoolType,
      carpools,
    })
    if (error) {
      setMsg(msg)
      setIsLoading('')
    } else {
      await onUpdate(() => {
        setMsg({ style: 'success', text: `${carpoolCountString} updated successfully.` })
        setIsLoading('')
      })
    }
  }

  // 6. Render
  const body = (
    <div className={isWeb ? 'm-1' : 'flex p-4 px-5 flex-col items-start gap-3 flex-[1_0_0] self-stretch'}>
      <div className='w-full'>
        <div className='flex justify-between items-start gap-2'>
          <div className='flex flex-col'>
            <h3 className='text-headings font-medium mb-0.5'>Your Schedule</h3>
            <p className='text-xs text-gray-600 leading-tight'>Tap dates to update multiple days</p>
          </div>
          <div className='flex flex-col items-center gap-1 flex-shrink-0 pl-2'>
            <div className='flex gap-1.5'>
              {Object.values(DefaultCarpoolType).map((type) => {
                const label = getCommunityCarpoolTypeLabel(type)
                const isSelected = type === selectedCarpoolType
                return (
                  <button
                    key={type}
                    onClick={() => setSelectedCarpoolType(type)}
                    title={`Switch to ${label}`}
                    className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                      isSelected
                        ? 'bg-custom-yellow border-custom-yellow text-gray-900 font-medium'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                    data-testid={`carpool-type-${type}`}
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      <div className='w-full'>
        <CalendarComponent
          type={CalendarType.CARPOOL}
          events={events}
          carpoolOptions={options}
          onCarpool={handleSetCustomSchedule}
        />
      </div>
    </div>
  )
  const rightElement = (
    <div className='grow-0 shrink min-w-0 max-w-[35%]'>
      <span
        className='inline-block max-w-full truncate text-xs text-gray-600 px-1 py-0.5 bg-gray-50 rounded'
        title={titleCaseSchoolNameForUI(comId)}
      >
        {titleCaseSchoolNameForUI(comId)}
      </span>
    </div>
  )

  return (
    <>
      {msg && <ToastComponent {...msg} onClose={() => setMsg(undefined)} />}

      {!isWeb ? (
        <div className='flex flex-col h-screen'>
          <BackComponent text='Assign Drivers' rightElement={rightElement} />
          {body}
        </div>
      ) : (
        <WizardComponent title='Assign Drivers' data_testId='modal-dialog-action-submit' rightElement={rightElement}>
          {body}
        </WizardComponent>
      )}
    </>
  )
}

function Index({
  user,
  students,
  onUpdate,
  failedAvatarUrls,
  onFailedAvatarUrl,
}: {
  user?: UserOutputProps
  students: StudentProps[]
  onUpdate: (cb?: () => void) => void
  failedAvatarUrls: Set<string>
  onFailedAvatarUrl: (url: string) => void
}): JSX.Element {
  const [searchParams] = useSearchParams()

  // Build props for child component
  const studentId = searchParams.get('id') ?? ''
  const student: StudentProps | undefined = students?.find((s: StudentProps) => s.studentId === studentId)

  if (!user || !studentId || !student) return <LinearProgress />
  return (
    <CarpoolManagementComponent
      studentId={studentId}
      user={user}
      userStudents={students}
      onUpdate={onUpdate}
      failedAvatarUrls={failedAvatarUrls}
      onFailedAvatarUrl={onFailedAvatarUrl}
    />
  )
}

export default Index
